import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import type { Settlement } from 'models/settlement';

import type {
    CustomPriceTarget,
    EventPositionVo,
    PositionVo,
    PriceMovementSubscription,
    ServerError,
} from '../../models';
import type { HistoricalPortfolioVo } from '../../models/portfolio';
import type {
    AddCustomPriceTargetRequest,
    AddPriceMovementSubscriptionRequest,
    CreateMarketRequestRequest,
    CreateMarketRequestResponse,
    DeleteCustomPriceTargetRequest,
    DeletePriceMovementSubscriptionRequest,
    GetAccountOrderHistoryResponse,
    GetAccountSettlementHistoryResponse,
    GetAccountTradeHistoryResponse,
    GetCustomPriceTargetsResponse,
    GetEventPositionResponse,
    GetEventPositionsParams,
    GetEventPositionsResponse,
    GetFavoritedSeriesListResponse,
    GetNettingSettingResponse,
    GetPositionLotsPnlAggregationRequest,
    GetPositionLotsPnlAggregationResponse,
    GetPriceMovementSubscriptionsResponse,
    GetRealizedPnlReportResponse,
    GetSampledUserPortfolioHistoryParams,
    GetSampledUserPortfolioHistoryResponse,
    GetUserMarketResponse,
    GetUserPortfolioCurrentResponse,
    GetUserSettlementHistoryParams,
    OrderHistory,
    PaginationParams,
    StartRealizedPnlReportRequest,
    StartRealizedPnlReportResponse,
    TradeHistory,
} from '../../models/reqres';
import { buildQueryString } from '../../utils/query';
import { exchangeApi, handleErrorCallback, noop, rateLimiter } from '../core';

export function getUserPosition(
    userId: string,
    marketId: string,
    onSuc: (userMarket: PositionVo) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetUserMarketResponse>(`/users/${userId}/positions/${marketId}`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.market_position);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getSampledUserPortfolioHistory(
    userId: string,
    onSuc: (prices: HistoricalPortfolioVo[]) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
    params?: GetSampledUserPortfolioHistoryParams,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    rateLimiter
        .getLimiter('getSampledUserPortfolioHistory')
        .schedule(() =>
            exchangeApi.get<GetSampledUserPortfolioHistoryResponse>(
                `/users/${userId}/sampled_portfolio${buildQueryString(params)}`,
                {
                    cancelToken: cancelRef.token,
                },
            ),
        )
        .then((response) => {
            onSuc(response.data.values || []);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getUserPortfolioCurrent(
    userId: string,
    onSuc: (price: HistoricalPortfolioVo) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    rateLimiter
        .getLimiter('getUserPortfolioHistory')
        .schedule(() =>
            exchangeApi.get<GetUserPortfolioCurrentResponse>(`/users/${userId}/portfolio/current_value`, {
                cancelToken: cancelRef.token,
            }),
        )
        .then((response) => {
            onSuc(response.data.value);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getEventPositions(
    userId: string,
    onSuc: (user_markets: Array<EventPositionVo>, cursor?: string) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
    params?: GetEventPositionsParams,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetEventPositionsResponse>(`/users/${userId}/event_positions${buildQueryString(params)}`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.event_positions || [], response.data.cursor);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getEventPosition(
    userId: string,
    eventTicker: string,
    onSuc: (user_markets: EventPositionVo) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetEventPositionResponse>(`/users/${userId}/event_positions/${eventTicker}`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.event_position);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getNettingSetting(
    userId: string,
    onSuc: (enabled: boolean) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetNettingSettingResponse>(`/users/${userId}/netting_enabled`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.enabled);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(onFinally);

    return cancelRef;
}

export function setNettingSetting(
    userId: string,
    enabled: boolean,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .put(`/users/${userId}/netting_enabled`, { enabled: enabled }, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(onFinally);

    return cancelRef;
}

export function getOrderHistory(
    userId: string,
    params: PaginationParams,
    onSuc: (orders: Array<OrderHistory>, cursor?: string) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetAccountOrderHistoryResponse>(`/users/${userId}/order_history`, {
            cancelToken: cancelRef.token,
            params: params,
        })
        .then((response) => {
            onSuc(response.data.orders, response.data.cursor);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getTradeHistory(
    userId: string,
    params: PaginationParams,
    onSuc: (trades: Array<TradeHistory>, cursor?: string) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetAccountTradeHistoryResponse>(`/users/${userId}/trade_history`, {
            cancelToken: cancelRef.token,
            params: params,
        })
        .then((response) => {
            onSuc(response.data.trades, response.data.cursor);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getSettlementHistory(
    userId: string,
    params: GetUserSettlementHistoryParams,
    onSuc: (settlements: Array<Settlement>, cursor?: string) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetAccountSettlementHistoryResponse>(`/users/${userId}/settlement_history`, {
            cancelToken: cancelRef.token,
            params: params,
        })
        .then((response) => {
            onSuc(response.data.settlements, response.data.cursor);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

// Realized PnL, aggregated per (subtrader, period). Not user-scoped in the
// path — the backend derives the caller from the session and validates that
// every requested subtrader is owned by them. `subtrader_ids` is a repeated
// query param, so build the query manually (buildQueryString/URLSearchParams
// would comma-join the array into a single value the backend can't parse).
export function getPositionLotsPnlAggregation(
    params: GetPositionLotsPnlAggregationRequest,
    onSuc: (response: GetPositionLotsPnlAggregationResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    const search = new URLSearchParams();
    params.subtrader_ids.forEach((id) => search.append('subtrader_ids', id));
    search.append('period_interval', params.period_interval);
    search.append('start_ts', params.start_ts);
    search.append('end_ts', params.end_ts);
    if (params.page_size !== undefined) {
        search.append('page_size', String(params.page_size));
    }
    if (params.cursor) {
        search.append('cursor', params.cursor);
    }

    exchangeApi
        .get<GetPositionLotsPnlAggregationResponse>(`/position_lots/pnl_aggregation?${search.toString()}`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            if (response.data) {
                onSuc(response.data);
            }
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function startRealizedPnlReport(
    userId: string,
    body: StartRealizedPnlReportRequest,
    onSuc: (response: StartRealizedPnlReportResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<StartRealizedPnlReportResponse>(`/users/${userId}/account/realized_pnl_reports`, body, {
            cancelToken: cancelRef.token,
            // TaxPage opens the download tab (focusing it, marking the app tab
            // hidden) before this POST runs. Without this flag, re-downloading a
            // year already fetched sends an identical URL/body that the request
            // interceptor cancels as a repeat-while-hidden request, so no new
            // report is ever started.
            params: { allowRequestEvenIfPageIsHidden: true },
        })
        .then((response) => {
            if (response.data) {
                onSuc(response.data);
            }
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getRealizedPnlReport(
    userId: string,
    reportId: string,
    onSuc: (response: GetRealizedPnlReportResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetRealizedPnlReportResponse>(`/users/${userId}/account/realized_pnl_reports/${reportId}`, {
            cancelToken: cancelRef.token,
            // Report generation polls the same URL repeatedly. Opening the
            // download tab focuses it and marks the app tab hidden, which the
            // request interceptor would otherwise use to cancel every repeat
            // poll (failing the download). Allow the poll to run while hidden.
            params: { allowRequestEvenIfPageIsHidden: true },
        })
        .then((response) => {
            if (response.data) {
                onSuc(response.data);
            }
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getPriceMovementSubscriptions(
    userId: string,
    onSuc: (priceMovementSubscriptions: Array<PriceMovementSubscription>) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetPriceMovementSubscriptionsResponse>(`/users/${userId}/notifications/price_movement_subscriptions`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.price_movement_subscriptions || []);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function addPriceMovementSubscription(
    userId: string,
    payload: AddPriceMovementSubscriptionRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post(`/users/${userId}/notifications/price_movement_subscriptions`, payload, {
            cancelToken: cancelRef.token,
        })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function deletePriceMovementSubscription(
    userId: string,
    payload: DeletePriceMovementSubscriptionRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete(`/users/${userId}/notifications/price_movement_subscriptions`, {
            cancelToken: cancelRef.token,
            data: payload,
        })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getCustomPriceTargets(
    userId: string,
    onSuc: (customPriceTargets: Array<CustomPriceTarget>) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetCustomPriceTargetsResponse>(`/users/${userId}/notifications/custom_price_targets`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.custom_price_targets || []);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function addCustomPriceTarget(
    userId: string,
    payload: AddCustomPriceTargetRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post(`/users/${userId}/notifications/custom_price_targets`, payload, {
            cancelToken: cancelRef.token,
        })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function deleteCustomPriceTarget(
    userId: string,
    payload: DeleteCustomPriceTargetRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete(`/users/${userId}/notifications/custom_price_targets`, {
            cancelToken: cancelRef.token,
            data: payload,
        })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createMarketRequest(
    userId: string,
    payload: CreateMarketRequestRequest,
    onSuc: (response: CreateMarketRequestResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post(`/users/${userId}/market_requests`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getFavoritedSeriesList(
    userId: string,
    onSuc: (response: GetFavoritedSeriesListResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetFavoritedSeriesListResponse>(`/users/${userId}/favorited_series`, { cancelToken: cancelRef.token })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function addToFavoritedSeriesList(
    userId: string,
    seriesTicker: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .put(`/users/${userId}/favorited_series/${seriesTicker}`, {}, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function removeFromFavoritedSeriesList(
    userId: string,
    seriesTicker: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete(`/users/${userId}/favorited_series/${seriesTicker}`, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}
